<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue';
import type { Ref } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { OBJECT } from '@pkg/config/sanity/schemas';
import type { Origin } from '@pkg/types/surpriseBag';
import NavDesktopHeaderInput from './NavDesktopHeaderInput.vue';
import NavSubmenuList from './NavSubmenuList.vue';
import type { SubmenuList } from '../types/mainNav';
import type { HeaderNavStyles, HeaderThemeState } from '../types/headerTypes';
import {
  buildHeaderStyles,
  DEFAULT_HEADER_THEME_STATE,
} from '../composables/useHeaderThemeDetection';
import { locationAutocompleteKey } from '../injectionKeys';

const locationApi = inject(locationAutocompleteKey, null);

const suggestions = computed(() => locationApi?.suggestions.value ?? []);
const loadingSuggestions = computed(() => locationApi?.loadingSuggestions.value ?? false);
const initialSearchValue = computed(() => locationApi?.initialSearchValue.value);

const injectedThemeState = inject<Ref<HeaderThemeState>>('headerThemeState');
const injectedHeaderStyles = inject<Ref<HeaderNavStyles>>('headerStyles');
const themeState = computed(() => injectedThemeState?.value ?? DEFAULT_HEADER_THEME_STATE);
const headerStyles = computed(
  () => injectedHeaderStyles?.value ?? buildHeaderStyles(themeState.value, false),
);

const emit = defineEmits<{
  'update:open': [value: boolean];
  'input-active': [value: boolean];
  'has-results': [value: boolean];
  closeNavMenu: [];
}>();

const { t } = useI18n();
const route = useRoute();

const autocompleteRef = ref<InstanceType<typeof NavDesktopHeaderInput> | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const wrapRef = ref<HTMLElement | null>(null);

function onWrapFocusOut(e: FocusEvent) {
  if ((e.relatedTarget as Node) && wrapRef.value?.contains(e.relatedTarget as Node)) { return; }
  emit('input-active', false);
}

const results = ref<Origin[]>([]);
const open = defineModel<boolean>('open', { default: false });
const loading = ref(false);

const PANEL_ID = 'nav-autocomplete-results-panel';
const TITLE_ID = 'nav-autocomplete-results-title';

function onExternalResults(payload: { results: Origin[]; open: boolean; loading: boolean }) {
  results.value = payload.results;
  open.value = payload.open;
  loading.value = payload.loading;
  emit('has-results', payload.results.length > 0);
}

const resultsAsSubmenuList = computed<SubmenuList>(() => ({
  submenuListTitle: TITLE_ID,
  submenuListItems: results.value.map(option => ({
    _type: OBJECT.NAVIGATION_LINK_EXTERNAL,
    title: option.displayName,
    url: locationApi?.buildFindUrl(option) ?? '',
  })),
}));

const hasResults = computed(() => resultsAsSubmenuList.value.submenuListItems.length > 0);

watch(hasResults, (val) => { emit('has-results', val); }, { immediate: true });

watch(
  () => route.path,
  () => {
    open.value = false;
  },
);

function getFirstLink(): HTMLAnchorElement | null {
  if (!panelRef.value) {
    return null;
  }
  const list = panelRef.value.querySelector('.nav__col-list');
  return (list?.querySelector('li:first-child a') ?? panelRef.value.querySelector('a')) as HTMLAnchorElement | null;
}

function onPanelKeydown(e: KeyboardEvent) {
  if (e.key !== 'Tab' || !e.shiftKey) { return; }
  const firstLink = getFirstLink();
  if (firstLink && e.target === firstLink) {
    e.preventDefault();
    autocompleteRef.value?.focusInput?.();
  }
}

function onResultsPanelClick() {
  open.value = false;
  emit('closeNavMenu');
}

const handleSelectedOption = (value: Origin | undefined) => {
  if (value != null && locationApi) {
    locationApi.handleLocationSelected(value, 'header');
  }
};

defineExpose({
  focusFirstResult() {
    getFirstLink()?.focus({ preventScroll: false });
  },
});
</script>

<template>
  <template v-if="locationApi">
    <div
      ref="wrapRef"
      class="nav-desktop-autocomplete__wrap"
      @mouseenter="emit('input-active', true); emit('closeNavMenu')"
      @mouseleave="emit('input-active', false)"
      @focusin="emit('input-active', true); emit('closeNavMenu')"
      @focusout="onWrapFocusOut"
    >
      <NavDesktopHeaderInput
        ref="autocompleteRef"
        :options="suggestions"
        :placeholder="t('headerAutocompletePlaceholder')"
        :input-aria-label="t('headerAutocompletePlaceholder')"
        :loading="loadingSuggestions"
        :results-panel-id="PANEL_ID"
        :text-color="themeState.textColor"
        :light-icon="themeState.textColor?.includes('dark')"
        :initial-search-value="initialSearchValue"
        @input-change="(v) => locationApi?.fetchSuggestions(v)"
        @selected="handleSelectedOption"
        @external-results="onExternalResults"
      />
    </div>
    <Teleport v-if="open" to="#header-viewport-content">
      <div
        :id="PANEL_ID"
        ref="panelRef"
        role="region"
        aria-label="Search results"
        class="nav-desktop-autocomplete__panel"
        @keydown="onPanelKeydown"
        @click.capture="onResultsPanelClick"
      >
        <div class="nav-content-slide-down">
          <div
            v-if="loading"
            class="nav-desktop-autocomplete__loading"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2 class="nav-desktop-autocomplete__loading-icon" aria-hidden="true" />
          </div>
          <template v-else-if="hasResults">
            <div
              class="nav-desktop-autocomplete__grid"
              :style="headerStyles.navGridStyle"
            >
              <div class="nav-desktop-autocomplete__col">
                <span
                  :id="TITLE_ID"
                  class="nav-desktop-autocomplete__sr-only"
                >
                  Search results
                </span>
                <NavSubmenuList :list="resultsAsSubmenuList" />
              </div>
            </div>
          </template>
          <p
            v-else
            class="nav-desktop-autocomplete__empty"
            aria-live="polite"
          >
            {{ t('headerNoLocationText') }}
          </p>
        </div>
      </div>
    </Teleport>
  </template>
</template>

<style scoped>
.nav-desktop-autocomplete__wrap {
  flex-shrink: 0;
  position: relative;
  margin-right: theme('spacing.16');
}

.nav-desktop-autocomplete__panel {
  width: 100%;
  min-height: 0;
  color: inherit;
  padding: theme('spacing.0') theme('spacing.40') theme('spacing.8') theme('spacing.16');
}

.nav-desktop-autocomplete__loading {
  display: flex;
  align-items: center;
  gap: theme('spacing.8');
  padding: theme('spacing.16');
  font-size: theme('fontSize.14');
}

.nav-desktop-autocomplete__loading-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: inherit;
  animation: nav-desktop-autocomplete-spin 1s linear infinite;
}

@keyframes nav-desktop-autocomplete-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.nav-desktop-autocomplete__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: theme('spacing.16') theme('spacing.24');
  padding: theme('spacing.24');
}

.nav-desktop-autocomplete__col {
  display: flex;
  flex-direction: column;
  gap: theme('spacing.8');
}

.nav-desktop-autocomplete__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.nav-desktop-autocomplete__empty {
  padding: theme('spacing.40') theme('spacing.16');
  margin: 0;
}

:deep(.c-nav-item--selected),
:deep(.c-nav-item:has(.router-link-active) .router-link-active) {
  text-decoration: none;
}
</style>
